import React from "react";
import { WhatsAppOutlined } from "@ant-design/icons";
import { Button } from "antd";
import {
  MessageSquare,
  Send,
  Zap,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import WhatsAppConnectImg from "../../assets/svg/whats-app-connect.png";

const featureList = [
  {
    icon: Send,
    title: "Send Messages",
    desc: "Reach prospects directly with high open rates.",
    color: "tw-bg-emerald-50 tw-text-emerald-600 tw-border-emerald-100",
  },
  {
    icon: TrendingUp,
    title: "Run Campaigns",
    desc: "Broadcast targeted outreach to lead lists.",
    color: "tw-bg-blue-50 tw-text-blue-600 tw-border-blue-100",
  },
  {
    icon: Zap,
    title: "Automate Flows",
    desc: "24/7 AI chatbot responses & smart routing.",
    color: "tw-bg-purple-50 tw-text-purple-600 tw-border-purple-100",
  },
  {
    icon: MessageSquare,
    title: "Manage Chats",
    desc: "Unified multi-agent inbox for sales teams.",
    color: "tw-bg-amber-50 tw-text-amber-600 tw-border-amber-100",
  },
];

const WhatsAppConnect = ({ onConnect }) => {
  return (
    <div className="tw-min-h-[calc(100vh-64px)] tw-w-full tw-bg-gradient-to-br tw-from-slate-50/80 tw-via-white tw-to-emerald-50/30 tw-p-6 sm:tw-p-10 lg:tw-p-12 tw-flex tw-items-center tw-justify-center !tw-font-inter tw-overflow-y-auto">
      <div className="tw-max-w-7xl tw-w-full tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-10 lg:tw-gap-8 tw-items-center">
        {/* Left Content Column */}
        <div className="lg:tw-col-span-7 tw-flex tw-flex-col tw-items-start">
          {/* Headline */}
          <h1 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-[44px] tw-font-extrabold tw-leading-[1.18] tw-text-slate-900 tw-tracking-tight tw-mb-4">
            Connect your{" "}
            <span className="tw-text-[#25D366]">
              WhatsApp Business
            </span>{" "}
            number
          </h1>

          {/* Subtitle */}
          <p className="tw-text-slate-600 tw-text-base sm:tw-text-lg tw-font-normal tw-leading-relaxed tw-mb-8 tw-max-w-xl">
            One unified workspace to broadcast targeted campaigns, automate customer inquiries with AI, and convert prospects into deals.
          </p>

          {/* 2x2 Feature Cards Grid */}
          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3.5 tw-w-full tw-max-w-xl tw-mb-8">
            {featureList.map((f) => (
              <div
                key={f.title}
                className="tw-flex tw-items-start tw-gap-3.5 tw-p-3.5 tw-rounded-2xl tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-shadow-xs hover:tw-shadow-md hover:tw-border-slate-300 tw-transition-all"
              >
                <div className={`tw-p-2.5 tw-rounded-xl tw-border tw-border-solid ${f.color} tw-shrink-0`}>
                  <f.icon className="tw-w-4 tw-h-4" />
                </div>
                <div>
                  <h4 className="tw-text-xs tw-font-bold tw-text-slate-800 tw-m-0">{f.title}</h4>
                  <p className="tw-text-[11px] tw-text-slate-500 tw-m-0 tw-mt-0.5 tw-leading-snug">
                    {f.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA & Trust Indicators */}
          <div className="tw-flex tw-flex-col tw-gap-3.5 tw-w-full tw-max-w-md">
            <button
              type="button"
              onClick={onConnect}
              className="tw-group tw-w-full tw-h-[52px] tw-px-6 tw-rounded-2xl !tw-bg-[#25D366] hover:!tw-bg-[#25D366] tw-text-white tw-font-bold tw-text-base tw-tracking-wide tw-cursor-pointer tw-flex tw-items-center tw-justify-between tw-border-0 tw-outline-none"
            >
              <div className="tw-flex tw-items-center tw-gap-3">
                <WhatsAppOutlined className="tw-text-xl tw-text-white" />
                <span>Connect WhatsApp Business</span>
              </div>

              <ArrowRight className="tw-w-5 tw-h-5 tw-text-white" />
            </button>

            {/* Trust Badges Bar */}
            <div className="tw-flex tw-items-center tw-justify-between tw-px-3.5 tw-py-2.5 tw-rounded-xl tw-bg-slate-50/90 tw-border tw-border-solid tw-border-slate-200/80 tw-text-[11px] tw-text-slate-600 tw-font-semibold">
              <span className="tw-flex tw-items-center tw-gap-1.5">
                <ShieldCheck className="tw-w-3.5 tw-h-3.5 tw-text-emerald-600" />
                Meta API Partner
              </span>
              <span className="tw-h-3.5 tw-w-px tw-bg-slate-200" />
              <span className="tw-flex tw-items-center tw-gap-1.5">
                <CheckCircle2 className="tw-w-3.5 tw-h-3.5 tw-text-emerald-600" />
                2-Min Setup
              </span>
              <span className="tw-h-3.5 tw-w-px tw-bg-slate-200" />
              <span className="tw-flex tw-items-center tw-gap-1.5">
                <CheckCircle2 className="tw-w-3.5 tw-h-3.5 tw-text-emerald-600" />
                Encrypted Data
              </span>
            </div>
          </div>
        </div>

        {/* Right Illustration Column */}
        <div className="lg:tw-col-span-5 tw-flex tw-justify-center tw-items-center tw-relative">
          {/* Ambient Glow Effects */}
          <div className="tw-absolute tw-w-72 tw-h-72 tw-bg-emerald-400/20 tw-rounded-full tw-blur-3xl tw-top-1/2 tw-left-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-pointer-events-none" />
          <div className="tw-absolute tw-w-60 tw-h-60 tw-bg-indigo-400/20 tw-rounded-full tw-blur-3xl tw-top-1/3 tw-left-1/3 tw-pointer-events-none" />

          <div className="tw-relative tw-w-full tw-max-w-md lg:tw-max-w-lg tw-flex tw-justify-center tw-items-center">
            <img
              src={WhatsAppConnectImg}
              alt="WhatsApp Business Connect"
              className="tw-w-full tw-h-auto tw-object-contain tw-drop-shadow-2xl tw-relative tw-z-10 tw-transition-transform hover:tw-scale-[1.02] tw-duration-300"
            />

            {/* Floating Badge 1 - Open Rate */}
            <div className="tw-absolute tw-top-2 -tw-left-2 lg:-tw-left-4 tw-z-20 tw-bg-white/95 tw-backdrop-blur-md tw-p-3 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-shadow-lg tw-flex tw-items-center tw-gap-3">
              <div className="tw-w-9 tw-h-9 tw-rounded-xl tw-bg-emerald-50 tw-text-emerald-600 tw-flex tw-items-center tw-justify-center tw-font-bold tw-text-sm">
                <TrendingUp className="tw-w-5 tw-h-5" />
              </div>
              <div>
                <div className="tw-text-xs tw-font-bold tw-text-slate-800">98% Open Rate</div>
                <div className="tw-text-[10px] tw-text-slate-500 tw-font-medium">High Engagement</div>
              </div>
            </div>

            {/* Floating Badge 2 - AI Auto-Reply */}
            <div className="tw-absolute tw-bottom-4 -tw-right-2 lg:-tw-right-4 tw-z-20 tw-bg-white/95 tw-backdrop-blur-md tw-p-3 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-shadow-lg tw-flex tw-items-center tw-gap-3">
              <div className="tw-w-9 tw-h-9 tw-rounded-xl tw-bg-indigo-50 tw-text-[#5D44FF] tw-flex tw-items-center tw-justify-center tw-font-bold tw-text-sm">
                <Zap className="tw-w-5 tw-h-5" />
              </div>
              <div>
                <div className="tw-text-xs tw-font-bold tw-text-slate-800">AI Auto-Reply</div>
                <div className="tw-text-[10px] tw-text-slate-500 tw-font-medium">24/7 Lead Qualify</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppConnect;
